import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, List, Typography } from '@/primer/components';
import type { ListItemProps } from '@/primer/components';
import { useAuth } from '@/api';
import { PageLayout } from '@/components/layout';

// ─── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <Typography
      variant="sectionHeader"
      size="medium"
      className="uppercase tracking-widest px-1 mb-2"
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Settings hub screen.
 *
 * The Account section and a log-out button are currently shown. Preferences and
 * Privacy and Data are hidden until their screens and policy links are ready
 * (PD-330326).
 */
export function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate({ to: '/' });
    } catch (err) {
      console.error('Failed to logout:', err);
      setIsLoggingOut(false);
    }
  };

  // ── ACCOUNT ──────────────────────────────────────────────────────────────

  const accountItems: ListItemProps[] = [
    {
      title: 'Profile',
      icon: 'user',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/profile' }),
    },
    {
      title: 'Connect socials',
      icon: 'instagram',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/connect' }),
    },
    {
      title: 'Shipping address',
      icon: 'truck',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/address' }),
    },
    {
      title: 'Email',
      icon: 'lock',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/email' }),
    },
  ];

  // TODO PD-330326: Preferences and Privacy and Data are hidden until their screens and policy links exist.
  // ── PREFERENCES ──────────────────────────────────────────────────────────

  // const preferenceItems: ListItemProps[] = [
  //   {
  //     title: 'Notifications',
  //     icon: 'bell',
  //     trailingIcon: 'chevron-right',
  //   },
  //   {
  //     title: 'App language',
  //     icon: 'speech-bubble-language',
  //     trailingIcon: 'chevron-right',
  //   },
  // ];

  // ── PRIVACY AND DATA ──────────────────────────────────────────────────────

  // const privacyItems: ListItemProps[] = [
  //   {
  //     title: 'Data consent',
  //     icon: 'id-card',
  //     trailingIcon: 'chevron-right',
  //   },
  //   {
  //     title: 'Data request',
  //     icon: 'send',
  //     trailingIcon: 'chevron-right',
  //   },
  //   {
  //     title: 'Delete account',
  //     icon: 'trash',
  //     trailingIcon: 'chevron-right',
  //   },
  //   {
  //     title: 'CCPA compliance',
  //     icon: 'document',
  //     trailingIcon: 'external-link',
  //   },
  //   {
  //     title: 'Terms of service',
  //     icon: 'document',
  //     trailingIcon: 'external-link',
  //   },
  //   {
  //     title: 'Privacy policy',
  //     icon: 'document',
  //     trailingIcon: 'external-link',
  //   },
  // ];

  return (
    <PageLayout className="py-7">
      <div className="flex flex-col gap-6">
        {/* ACCOUNT */}
        <section>
          <SectionLabel testId="section-account">Account</SectionLabel>
          <List items={accountItems} />
        </section>

        {/*
        PREFERENCES
        <section>
          <SectionLabel testId="section-preferences">Preferences</SectionLabel>
          <List items={preferenceItems} />
        </section>

        PRIVACY AND DATA
        <section>
          <SectionLabel testId="section-privacy">Privacy and Data</SectionLabel>
          <List items={privacyItems} />
        </section>
        */}

        {/* LOG OUT */}
        <Button fullWidth onClick={handleLogout} isLoading={isLoggingOut}>
          Log out
        </Button>
      </div>
    </PageLayout>
  );
}

export default Settings;
